/**
 * VendorBuyingModeModal — asked once, the first time you order from a vendor.
 *
 * "Order" means two completely different things depending on the vendor: an
 * emailed purchase order for someone who delivers, or a shopping list for
 * somewhere you drive to. Countertop cannot guess, and guessing wrong sends a
 * real email to a real supplier — so until somebody says, the order button
 * reads a neutral "Order" and this asks on the way through.
 *
 * It appears when `vendors.fulfillment_type_source` is 'default' (see
 * services/vendorFulfillmentService). Answering writes 'user', and it never
 * appears for that vendor again. There is deliberately no "skip" that still
 * places the order: an order created without an answer would ask again next
 * time, which is the nagging this exists to prevent. Cancel abandons the order.
 *
 * One modal covers both shapes:
 *   - one vendor  → two big choice buttons (finishing a single vendor)
 *   - several     → one row per vendor with a two-way toggle, then one button,
 *                   because a queue of modals is not a flow, it is a punishment
 *                   (the Purchase Planner's "create the remaining orders",
 *                   where several new vendors surface at once).
 *
 * The answer is the LAST interaction, never a setting to come back from: the
 * caller resumes the finish it deferred, so pressing the answer creates the
 * order(s). `note` says so on the modal.
 */
import React, { useMemo, useState } from 'react';
import { DELIVERY, PICKUP, BUYING_MODE_ANSWERS } from '../../utils/buyingMode';

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)', zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
  paddingLeft: 20, paddingRight: 20,
};
const boxStyle = {
  background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460,
  maxHeight: '100%', overflowY: 'auto',
  boxShadow: '0 8px 40px rgba(43,29,20,0.20)', fontFamily: 'Open Sans, sans-serif',
};
const titleStyle = { fontWeight: 800, fontSize: 17, color: '#2b1d14', marginBottom: 6 };
const leadStyle = { fontSize: 13, color: '#5A4A42', lineHeight: 1.5, margin: '0 0 18px' };
const choiceStyle = (accent) => ({
  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
  background: '#fff', color: '#2b1d14', border: `1.5px solid ${accent}`,
  borderRadius: 10, padding: '12px 14px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
});
const choiceSubStyle = { display: 'block', fontWeight: 400, fontSize: 12.5, color: '#7b6b61', marginTop: 3 };
const cancelStyle = {
  background: 'none', border: 'none', color: '#7b6b61', fontWeight: 600, fontSize: 13,
  cursor: 'pointer', fontFamily: 'inherit', padding: '8px 0', marginTop: 4,
};
const continueStyle = (enabled) => ({
  width: '100%', cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.55,
  background: '#5A4A42', color: '#fff', border: '1.5px solid #5A4A42', borderRadius: 10,
  padding: '11px 14px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, marginTop: 6,
});
const rowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
  padding: '10px 0', borderBottom: '1px solid #f2ece6', flexWrap: 'wrap',
};
const toggleStyle = (active) => ({
  cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
  padding: '6px 10px', borderRadius: 8, whiteSpace: 'nowrap',
  background: active ? '#5A4A42' : '#fff',
  color: active ? '#fff' : '#5A4A42',
  border: `1.5px solid ${active ? '#5A4A42' : '#d5c9c0'}`,
});
const noteStyle = {
  fontSize: 12.5, color: '#5A4A42', lineHeight: 1.5, margin: '-10px 0 16px',
  background: '#F6F1EC', border: '1px solid #E7DDD3', borderRadius: 8, padding: '8px 11px',
};
const errorStyle = {
  background: '#FDECEA', border: '1px solid #f5c2bd', color: '#b71c1c',
  borderRadius: 8, padding: '9px 12px', fontSize: 12.5, marginBottom: 12,
};

// The two answers, from the one place they are written down — the same strings
// the vendor editor's dropdown offers. Two copies of a question's wording is
// two questions.
export const DELIVERY_COPY = BUYING_MODE_ANSWERS.find((a) => a.mode === DELIVERY);
export const PICKUP_COPY = BUYING_MODE_ANSWERS.find((a) => a.mode === PICKUP);

/**
 * @param {Array<{id, name}>} vendors - the vendors with no answer yet
 * @param {(answers: Array<[id, mode]>) => Promise<void>} onAnswer - must write
 *        the answers; rejecting keeps the modal open and shows the message.
 * @param {() => void} onCancel - abandons the order entirely.
 * @param {string} note - what answering DOES, when the answer is also the last
 *        interaction. The question is only ever asked because somebody pressed
 *        Finish, so the answer creates the order; saying so is the difference
 *        between a setting and a decision.
 * @param {string} continueLabel - the multi-vendor button, when 'Continue'
 *        would understate what it is about to do.
 */
export default function VendorBuyingModeModal({
  vendors = [],
  onAnswer,
  onCancel,
  note = '',
  continueLabel = 'Continue',
}) {
  const list = useMemo(
    () => vendors.filter((v) => v && v.id != null).map((v) => ({ id: String(v.id), name: v.name || 'This vendor' })),
    [vendors],
  );
  const [choices, setChoices] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!list.length) return null;
  const single = list.length === 1;

  const submit = async (answers) => {
    setSaving(true);
    setError('');
    try {
      await onAnswer(answers);
    } catch (err) {
      // The answer did not stick, so the order must not proceed — say so and
      // stay put rather than silently ordering under a guess.
      setError(err?.message || 'Could not save that. Please try again.');
      setSaving(false);
    }
  };

  const allAnswered = list.every((v) => choices[v.id]);

  return (
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label="How do you buy from this vendor?"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div style={boxStyle}>
        <div style={titleStyle}>
          {single ? `How do you buy from ${list[0].name}?` : 'How do you buy from these vendors?'}
        </div>
        <p style={leadStyle}>
          {single
            ? 'You only have to answer this once — we will remember it for every future order. You can change it any time in the Vendor Editor.'
            : 'These vendors are new to ordering. Answer once and we will remember for next time.'}
        </p>

        {note && <p style={noteStyle} data-testid="buying-mode-note">{note}</p>}

        {error && <div style={errorStyle} role="alert">{error}</div>}

        {single ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              style={choiceStyle('#5A4A42')}
              disabled={saving}
              data-testid="buying-mode-delivery"
              onClick={() => submit([[list[0].id, DELIVERY]])}
            >
              {DELIVERY_COPY.label}
              <span style={choiceSubStyle}>{DELIVERY_COPY.sub}</span>
            </button>
            <button
              type="button"
              style={choiceStyle('#7b6b61')}
              disabled={saving}
              data-testid="buying-mode-pickup"
              onClick={() => submit([[list[0].id, PICKUP]])}
            >
              {PICKUP_COPY.label}
              <span style={choiceSubStyle}>{PICKUP_COPY.sub}</span>
            </button>
            <button type="button" style={cancelStyle} disabled={saving} onClick={onCancel}>
              {saving ? 'Saving…' : 'Cancel'}
            </button>
          </div>
        ) : (
          <div>
            {list.map((vendor) => (
              <div key={vendor.id} style={rowStyle} data-testid="buying-mode-vendor-row">
                <span style={{ fontWeight: 700, fontSize: 13.5, color: '#2b1d14', flex: '1 1 140px', minWidth: 0 }}>
                  {vendor.name}
                </span>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    style={toggleStyle(choices[vendor.id] === DELIVERY)}
                    aria-pressed={choices[vendor.id] === DELIVERY}
                    data-testid="buying-mode-toggle-delivery"
                    disabled={saving}
                    onClick={() => setChoices((c) => ({ ...c, [vendor.id]: DELIVERY }))}
                  >
                    They deliver
                  </button>
                  <button
                    type="button"
                    style={toggleStyle(choices[vendor.id] === PICKUP)}
                    aria-pressed={choices[vendor.id] === PICKUP}
                    data-testid="buying-mode-toggle-pickup"
                    disabled={saving}
                    onClick={() => setChoices((c) => ({ ...c, [vendor.id]: PICKUP }))}
                  >
                    We go there
                  </button>
                </span>
              </div>
            ))}
            <button
              type="button"
              style={continueStyle(allAnswered && !saving)}
              disabled={!allAnswered || saving}
              data-testid="buying-mode-continue"
              onClick={() => submit(list.map((v) => [v.id, choices[v.id]]))}
            >
              {saving ? 'Saving…' : continueLabel}
            </button>
            <button type="button" style={cancelStyle} disabled={saving} onClick={onCancel}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
